import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  styled,
  Grid,
} from "@mui/material";
import { FilterList, ViewModule } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { useLeadsData } from "../../hooks/useContext/leadsData";
import {
  CLOSE_DEAL_DONE_TYPE,
  CLOSE_UNSUCCESSFULL_TYPE,
} from "../../constants/leads";
import { PROPERTY_LEADS_COLUMNS } from "./leadsData";
import { useLocation } from "react-router-dom";
import {
  useGetAgentsQuery,
  useLeadStatusQuery,
} from "../../redux/api/leadsApi";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";

// ✅ Table Component
const LeadTable = ({ leads, isLoading, columns }) => {
  const isMobileScreen = useMediaQuery("(max-width:768px)");

  const getRowBackgroundColor = (leadStatus) => {
    if (leadStatus === CLOSE_DEAL_DONE_TYPE) return "#d4edda";
    if (leadStatus === CLOSE_UNSUCCESSFULL_TYPE) return "#f8d7da";
    return "";
  };

  return (
    <MaterialReactTable
      enableRowActions={false}
      columns={columns}
      data={leads}
      getRowId={(row) => row.leadid}
      state={{ showSkeletons: isLoading }}
      initialState={{ density: "compact", pagination: { pageSize: 20 } }}
      enableDensityToggle={false}
      enableStickyHeader={!isMobileScreen}
      muiTableContainerProps={{
        sx: { maxHeight: isMobileScreen ? "100%" : "calc(100vh - 320px)" },
      }}
      muiTableBodyRowProps={({ row }) => ({
        sx: { backgroundColor: getRowBackgroundColor(row.original.leadStatus) },
      })}
    />
  );
};

// ✅ Main Component
export default function AllLead() {
  const { agencyLeads, LeadsLoading, refetch } = useLeadsData();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const [selectedAgent, setSelectedAgent] = useState("all"); // 🧩 NEW

  const leadStatus = queryParams.get("status");
  const agentId = queryParams.get("agentid");
  const filter = queryParams.get("filter");
  const leadId = queryParams.get("leadId");
  const fromDate = queryParams.get("fromDate");
  const toDate = queryParams.get("toDate");

  //  MUI Dropdown Filters
  const { data: STATUS = [] } = useLeadStatusQuery();
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDealFilter, setSelectedDealFilter] = useState("all");
  const currentAgency = useSelector(selectCurrentagency);

  const { data: allAgents = [] } = useGetAgentsQuery(currentAgency?.agencyid);

  // ✅ Combined Filter Logic
  const filteredLeads = useMemo(() => {
    let leads =
      agencyLeads?.filter((lead) => {
        const matchesStatus = leadStatus
          ? lead.leadStatus?.toString() === leadStatus
          : true;
        const matchesAgent = agentId
          ? lead.agentid?.toString() === agentId
          : true;
        const matchesLeadId = leadId
          ? lead.leadid?.toString() === leadId
          : true;

        let dateFilter = true;

        if (filter === "today") {
          const today = new Date();
          const updated = new Date(lead.leadUpdatedDate);

          dateFilter =
            today.getFullYear() === updated.getFullYear() &&
            today.getMonth() === updated.getMonth() &&
            today.getDate() === updated.getDate();
        } else if (filter === "custom" && fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);

          // Normalize to cover the entire "to" day
          from.setHours(0, 0, 0, 0);
          to.setHours(23, 59, 59, 999);

          const updated = new Date(lead.leadUpdatedDate);
          dateFilter = updated >= from && updated <= to;
        } else if (filter === "Overdue") {
          // Overdue = follow-up date is before now
          const now = new Date();

          // make sure followupdate exists and is a valid date
          if (lead.followupdate) {
            const followup = new Date(lead.followupdate);

            // Only include if follow-up date is valid and in the past
            if (!isNaN(followup.getTime())) {
              dateFilter = followup < now;
            } else {
              // invalid date, skip this lead
              dateFilter = false;
            }
          } else {
            // no follow-up date, skip this lead
            dateFilter = false;
          }
        }

        return matchesStatus && matchesAgent && matchesLeadId && dateFilter;
      }) || [];

    // ✅ Hide closed leads (successful + unsuccessful) by default
    if (selectedStatus === "all" && selectedDealFilter === "all") {
      leads = leads.filter(
        (lead) =>
          lead.leadStatus !== CLOSE_DEAL_DONE_TYPE &&
          lead.leadStatus !== CLOSE_UNSUCCESSFULL_TYPE
      );
    }

    // ✅ Apply Status Filter
    if (selectedStatus !== "all") {
      leads = leads.filter(
        (lead) => Number(lead.leadStatus) === Number(selectedStatus)
      );
    }

    // ✅ Apply Deal Filter
    if (selectedDealFilter === "successful") {
      leads = leads.filter((lead) => lead.leadStatus === CLOSE_DEAL_DONE_TYPE);
    } else if (selectedDealFilter === "unsuccessful") {
      leads = leads.filter(
        (lead) => lead.leadStatus === CLOSE_UNSUCCESSFULL_TYPE
      );
    }

    // ✅ 🆕 Apply Agent Filter (same as first file)
    if (selectedAgent !== "all") {
      leads = leads.filter(
        (lead) => Number(lead.agentid) === Number(selectedAgent)
      );
    }

    return leads;
  }, [
    agencyLeads,
    leadStatus,
    agentId,
    leadId,
    filter,
    fromDate,
    toDate,
    selectedStatus,
    selectedDealFilter,
    selectedAgent,
  ]);

  return (
    <div>
      {/* Header */}
      <StyledHeader>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Property Leads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and track property-based leads
          </Typography>
        </Box>
        <Chip
          label={`${filteredLeads.length} leads`}
          color="primary"
          variant="outlined"
          size="small"
        />
      </StyledHeader>

      {/* Filters */}
      <Box mb={2}>
        <FilterHeader>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FilterList color="primary" />
            <Typography variant="h6" component="h2" sx={{ ml: 1 }}>
              Filters
            </Typography>
          </Box>
        </FilterHeader>

        <FilterContent>
          {/* 🟡 Agent Filter */}
          <FormControl size="small" sx={{ ...filterSx, width: 160 }}>
            <InputLabel>Agent</InputLabel>
            <Select
              label="Agent"
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    width: 160,
                    "& .MuiMenuItem-root": {
                      whiteSpace: "normal", // ✅ allows wrapping
                      wordBreak: "break-word", // ✅ break long words if needed
                      lineHeight: 1.3, // ✅ better spacing for 2 lines
                      py: 0.5,
                    },
                  },
                },
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value="all">All Agents</MenuItem>
              {allAgents
                ?.filter((agent) => agent.status === 1) // ✅ show only active agents
                .map((agent) => (
                  <MenuItem key={agent.agentid} value={agent.agentid}>
                    {agent.agentname}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* 🟢 Lead Status Filter */}
          <FormControl size="small" sx={{ ...filterSx, width: 160 }}>
            <InputLabel>Lead Status</InputLabel>
            <Select
              label="Lead Status"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                if (selectedDealFilter !== "all") setSelectedDealFilter("all");
              }}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    width: 160,
                    "& .MuiMenuItem-root": {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.3,
                      py: 0.5,
                    },
                  },
                },
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value="all">Total Leads</MenuItem>
              {STATUS.map((status) => (
                <MenuItem key={status.statusId} value={status.statusId}>
                  {status.statusName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 🟣 Deal Type Filter */}
          <FormControl size="small" sx={{ ...filterSx, width: 160 }}>
            <InputLabel>Deal Status</InputLabel>
            <Select
              label="Deal Status"
              value={selectedDealFilter}
              onChange={(e) => {
                setSelectedDealFilter(e.target.value);
                if (selectedStatus !== "all") setSelectedStatus("all");
              }}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    width: 160,
                    "& .MuiMenuItem-root": {
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.3,
                      py: 0.5,
                    },
                  },
                },
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null,
              }}
            >
              <MenuItem value="all">Closed Leads</MenuItem>
              <MenuItem value="successful">Won Leads</MenuItem>
              <MenuItem value="unsuccessful">Lost Leads</MenuItem>
            </Select>
          </FormControl>
          <Grid item>
            <Button
              variant="contained"
              size="small"
              sx={{ color: "white" }}
              onClick={() => {
                if (!filteredLeads?.length) {
                  alert("No leads data available to export!");
                  return;
                }

                // ✅ Step 1: Get columns excluding non-exportable ones
                let columns = PROPERTY_LEADS_COLUMNS(refetch).filter(
                  (col) =>
                    col.accessorKey &&
                    !["edit", "history", "propertydata"].includes(
                      col.accessorKey
                    )
                );

                // ✅ Step 1.5: Add description if missing
                const hasDescription = columns.some(
                  (col) => col.accessorKey === "description"
                );
                if (!hasDescription) {
                  columns.push({
                    accessorKey: "description",
                    header: "Description",
                  });
                }

                // ✅ Step 1.6: Add agentname as FIRST column
                columns.unshift({
                  accessorKey: "agentname",
                  header: "Agent Name",
                });

                // ✅ Step 2: Build export-ready array
                const exportData = filteredLeads.map((lead) => {
                  const row = {};
                  columns.forEach((col) => {
                    const key = col.accessorKey;
                    const header = col.header;
                    let value = lead[key];

                    // ✅ Map status / leadStatus to readable name
                    if (key === "leadStatus" || key === "status") {
                      value = lead.statusName || "";
                    }

                    // ✅ Include agent name
                    if (key === "agentname") {
                      value = lead.agentname || "";
                    }

                    // ✅ Description handling
                    if (key === "description") {
                      value = lead.description || "";
                    }

                    // ✅ Format date fields
                    if (
                      key === "leadTime" ||
                      key === "followupdate" ||
                      key === "leadUpdatedDate"
                    ) {
                      value = value ? new Date(value).toLocaleString() : "";
                    }

                    // ✅ Combine first + last name
                    if (key === "firstname") {
                      value = `${lead.firstname || ""} ${
                        lead.lastname || ""
                      }`.trim();
                    }

                    // ✅ Lead Tag formatting
                    if (key === "leadTag") {
                      value = `L-${lead.leadid}`;
                    }

                    row[header] = value ?? "";
                  });
                  return row;
                });

                // ✅ Step 3: Convert to worksheet
                const worksheet = XLSX.utils.json_to_sheet(exportData);

                // ✅ Bold header row
                const range = XLSX.utils.decode_range(worksheet["!ref"]);
                for (let C = range.s.c; C <= range.e.c; ++C) {
                  const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                  if (!worksheet[cellAddress]) continue;
                  worksheet[cellAddress].s = { font: { bold: true } };
                }

                // ✅ Optional: Auto width for all columns
                const colWidths = Object.keys(exportData[0] || {}).map(
                  (key) => ({
                    wch: Math.max(key.length + 5, 15),
                  })
                );
                worksheet["!cols"] = colWidths;

                // ✅ Step 4: Export as Excel
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
                XLSX.writeFile(workbook, "AllLeadsData.xlsx");
              }}
            >
              Export All Leads
            </Button>
          </Grid>
        </FilterContent>
      </Box>

      {/* Table */}
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <LeadTable
          columns={PROPERTY_LEADS_COLUMNS(refetch)}
          leads={filteredLeads}
          isLoading={LeadsLoading}
        />
      </Box>
    </div>
  );
}

// ✅ Styled Components
const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

const FilterHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

const FilterContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

const TableContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  overflow: "hidden",
  border: `1px solid ${theme.palette.divider}`,
}));

const filterSx = {
  minWidth: 140,
  "& .MuiInputLabel-root": { fontSize: 13 },
  "& .MuiSelect-select": {
    py: 0.5,
    fontSize: 13,
    height: "28px",
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
  },
};
